import React, { useEffect, useState } from 'react';
import { Server, Loader2, Search, Ban, ShieldCheck } from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';
import api from '../../lib/api';

const AdminServers: React.FC = () => {
  const { servers, isLoadingServers, fetchServers, banServer, unbanServer } = useAdminStore();
  const [search, setSearch] = useState('');
  const [banModal, setBanModal] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [banReasons, setBanReasons] = useState<string[]>([]);

  useEffect(() => {
    fetchServers();
    api.get('/admin/servers/ban-reasons').then(({ data }) => setBanReasons(data)).catch(() => {});
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchServers(search);
  };

  const handleBan = async () => {
    if (!banModal || !banReason) return;
    const ok = await banServer(banModal, banReason, notify);
    if (ok) { setBanModal(null); setBanReason(''); }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-text-primary">Serveurs</h2>
            <p className="text-xs text-text-muted mt-0.5">Gérez les serveurs de la plateforme</p>
          </div>
          <form onSubmit={handleSearch} className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-bg-tertiary border border-border-color text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-admin-accent/50"
            />
          </form>
        </div>

        {isLoadingServers ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-admin-accent" />
          </div>
        ) : servers.length === 0 ? (
          <div className="text-center py-16">
            <Server className="h-10 w-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-sm text-text-muted">Aucun serveur trouvé</p>
          </div>
        ) : (
          <div className="space-y-1">
            {servers.map((s) => (
              <div key={s.id} className="flex items-center gap-3 px-4 py-3 bg-bg-secondary/40 rounded-xl hover:bg-bg-secondary/60 transition-colors">
                {s.icon_url ? (
                  <img src={s.icon_url} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-admin-accent/10 flex items-center justify-center text-sm font-bold text-admin-accent flex-shrink-0">
                    {s.name[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary truncate">{s.name}</span>
                    {s.is_banned && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-error/15 text-error">Banni</span>}
                  </div>
                  <p className="text-xs text-text-muted">@{s.owner_username} · {s.member_count} membres · {new Date(s.created_at).toLocaleDateString('fr-CA')}</p>
                </div>
                {s.is_banned ? (
                  <button
                    onClick={() => unbanServer(s.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-success/10 text-success text-xs font-medium hover:bg-success/20"
                  >
                    <ShieldCheck className="h-3 w-3" /> Débannir
                  </button>
                ) : (
                  <button
                    onClick={() => setBanModal(s.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-error/10 text-error text-xs font-medium hover:bg-error/20"
                  >
                    <Ban className="h-3 w-3" /> Bannir
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Ban modal */}
        {banModal && (
          <div className="fixed inset-0 bg-black/60 z-[300] flex items-center justify-center" onClick={() => setBanModal(null)}>
            <div className="bg-bg-secondary rounded-xl border border-border-color p-6 w-96 space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-bold text-text-primary">Bannir le serveur</h3>
              <div className="space-y-2">
                {banReasons.map((r) => (
                  <button
                    key={r}
                    onClick={() => setBanReason(r)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${
                      banReason === r ? 'border-admin-accent bg-admin-accent/10 text-admin-accent' : 'border-border-color text-text-secondary hover:border-text-muted'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-text-secondary">
                <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="rounded" />
                Notifier les membres
              </label>
              <div className="flex gap-2">
                <button onClick={() => setBanModal(null)} className="flex-1 px-3 py-2 rounded-lg bg-bg-tertiary text-text-secondary text-sm hover:bg-highlight">Annuler</button>
                <button onClick={handleBan} disabled={!banReason} className="flex-1 px-3 py-2 rounded-lg bg-error text-white text-sm font-medium disabled:opacity-50">Bannir</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServers;
